(ns hai.home.mobile.mini-apps.tasks
  "My Tasks mini-app card for the mobile dashboard: the same task data as the
   full Tasks page, grouped into collapsible Now / Next / Later sections. The ＋
   swaps the card into Add-a-task mode — the header retitles and the body
   becomes the title form ([[hai.tasks.form]]); the title links to the full
   Tasks page. Shares the task row with the desktop card via [[hai.tasks.card]]
   and the tinted header row via [[hai.tasks.header]]; the card frame stays
   mobile's own."
  (:require
   [hai.entity.data :as entity-data]
   [hai.home.mobile.mini-apps :as mini-apps]
   [hai.mini-apps.data :as mini-apps-data]
   [hai.mini-apps.frame :as frame]
   [hai.spaces.data :as spaces-data]
   [hai.tasks.add-pill :as add-pill]
   [hai.tasks.assignee-picker :as assignee-picker]
   [hai.tasks.card :as task-card]
   [hai.tasks.data :as data]
   [hai.tasks.form :as form]
   [hai.tasks.header :as header]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.web-app.routing :as routing]
   [humbleai.belt.set :as set]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

;; The frame stays mobile's own — softer shadow, 20px radius, touch-sized
;; targets — but the header row is the shared one every tasks surface uses.
(o/defstyled card mini-apps/card
  ;; Collapsed, the bar is the whole card, so it rounds its bottom corners too.
  [:&.collapsed
   [header/bar
    {:border-bottom-left-radius "20px"
     :border-bottom-right-radius "20px"}]]
  [header/bar
   {:border-top-left-radius "20px"
    :border-top-right-radius "20px"}
   ;; Touch targets, as with the row's edit/delete below.
   [:.bar-action-btn {:width "32px" :height "32px"}]
   ;; The tasks card wears the shared tasks header, not the mobile card frame's
   ;; own, so the frame's pill sizing doesn't reach it from here.
   [add-pill/pill
    {:height "38px"}
    [:.add-action {:height "30px"}]
    [:.add-menu {:width "30px" :height "30px"}]]]
  ;; Rows float as spaced cards rather than a bordered list, so the raised ones
  ;; read as pressable. The inset holds them off the section edges and gives the
  ;; drop shadows room.
  [:.section-tasks
   {:display "flex" :flex-direction "column" :gap "8px"
    :padding "4px 14px 16px"}]
  ;; The shared frame clips the last section to round the card's bottom; with
  ;; inset floating cards there is nothing to clip, and the clip would eat their
  ;; shadows. Let it overflow instead.
  [mini-apps/section
   [:&:last-child
    {:overflow "visible"}
    ;; Without that clip a collapsed last section sits on the card's bottom edge
    ;; with nothing rounding it, so its hover fill rounds its own corners. Open,
    ;; the rows are what reach the edge and the toggle stays square.
    [mini-apps/section-toggle
     ["&:not(.open)"
      {:border-bottom-left-radius "20px"
       :border-bottom-right-radius "20px"}]]]]
  ;; Roomier edit/delete tap targets on touch (the shared row uses 26px for the
  ;; desktop pointer).
  [task-card/task-row
   [:.icon-btn {:width "32px" :height "32px"}]])

;; Links the add/save toasts to the task. Home lists tasks rather than showing
;; one, so the link always has somewhere to go.
(def ^:private task-link
  {:href-fn #(routing/href :tasks/detail {:task/uuid %})
   :target routing/link-target})

(defn- section-view [{:keys [label tasks open? on-toggle]}]
  [mini-apps/section-view
   {:label label :item-count (count tasks) :open? open? :on-toggle on-toggle}
   (when (seq tasks)
     (into [:div.section-tasks]
           (map (fn [task]
                  ^{:key (:task/uuid task)}
                  [task-card/task-row-component task
                   {:hover-actions? false
                    :show-hint? false
                    :spaces? @(<s [::spaces-data/enabled?])
                    :task-link task-link
                    :on-open-entity entity-data/open-entity}]))
           tasks))])

(defn panel [{title :mini-app/title}]
  (r/with-let [!open (r/atom #{:now})
               toggle-section (fn [id] (swap! !open set/toggle-membership id))]
    (let [buckets @(<s [::data/buckets])
          loaded? @(<s [::data/loaded?])
          adding? @(<s [::data/composer-open?])
          saving? (= :start @(<s [:fetch/state ::create]))
          default-urgency @(<s [::data/default-urgency])
          collapsed? @(<s [::mini-apps-data/collapsed? :tasks])
          open @!open
          total (reduce + (map count (vals buckets)))]
      (if adding?
        [card
         [header/bar {:title "Add a task"}
          [:button.bar-action-btn
           {:type "button"
            :aria-label "Close"
            :title "Close"
            :on-click #(>e [::data/close-composer])}
           [icons/outlined-close {:size 18}]]]
         [form/task-form {:draft-id data/composer-draft-id
                          :default-urgency default-urgency
                          :saving? saving?
                          :on-save (fn [staged]
                                     (>e [::data/create (assoc staged
                                                               :request-id ::create
                                                               :task-link task-link
                                                               :success-fx [[:dispatch [::data/close-composer]]])])
                                     (swap! !open conj (data/staged-bucket staged)))
                          :on-cancel #(>e [::data/close-composer])
                          :on-close #(>e [::data/close-composer])
                          :show-hint? false
                          :render-assignee-picker
                          (fn [{:keys [on-pick trigger]}]
                            [assignee-picker/component
                             {:editable? true
                              :on-pick on-pick
                              :label "Assign"
                              :trigger trigger}])}]]
        [card {:class (when collapsed? "collapsed")}
         [header/bar
          {:title title
           :href (routing/href :tasks/view)
           :target routing/link-target}
          [add-pill/component
           {:on-add (fn []
                      (>e [::mini-apps-data/expand :tasks])
                      (>e [::data/open-composer (form/from-task nil default-urgency)]))}]
          [frame/collapse-toggle
           {:collapsed? collapsed?
            :label      "tasks"
            :class      "bar-action-btn"
            :on-toggle  #(>e [::mini-apps-data/toggle-collapsed :tasks])}]]
         (when-not collapsed?
           (cond
             (not loaded?) nil
             (zero? total) [:div.app-placeholder "Nothing yet — add your first task above."]
             :else
             (into [:div.task-sections]
                   (map (fn [{:keys [id label]}]
                          ^{:key id}
                          [section-view {:label label
                                         :tasks (get buckets id)
                                         :open? (contains? open id)
                                         :on-toggle #(toggle-section id)}]))
                   data/bucket-tabs)))]))))
